import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatChipListbox } from '@angular/material/chips';
import { isObject } from '@libraries/helpers';
import { take } from 'rxjs/operators';

interface SelectBubbleObject {
  label?: string;
  display: any;
  value: string | number | boolean;
}

type SelectBubbleItem = string | number | boolean | SelectBubbleObject;

@Component({
  selector: 'select-bubbles',
  templateUrl: './select-bubbles.component.html',
  styleUrls: ['./select-bubbles.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectBubblesComponent),
      multi: true,
    },
  ],
})
export class SelectBubblesComponent implements ControlValueAccessor, AfterViewInit {
  @Input() translate = false;

  @Input()
  set items(items: SelectBubbleItem[]) {
    const mappedItems = items.map((item) => {
      if (isObject(item) && item.hasOwnProperty('display') && item.hasOwnProperty('value')) {
        return item as SelectBubbleObject;
      }
      return { display: <any>item, value: <any>item };
    });
    this._items = mappedItems;
  }
  _items: SelectBubbleObject[] = [];

  @Input() selected: Array<any> = [];
  @Input() disabled = false;

  @Input() multi = false;

  // only use when multi = false;
  // writes/outputs value as a primitive value instead of an array. ex [selectedOption1] vs selectedOption1
  @Input() singleValue = false;

  @Output() bubbleToggle = new EventEmitter<Array<any>>();
  _onChange: (_: any) => void;
  _onTouched: () => void;
  _disabled: boolean;

  @ViewChild('chips', { static: true }) chips: MatChipListbox;

  constructor() {}

  ngAfterViewInit() {
    this.chips.chipBlurChanges.pipe(take(1)).subscribe((e) => {
      if (this._onTouched) {
        this._onTouched();
      }
    });
  }

  // Writes a new value to the element.
  writeValue(value: any): void {
    if (value || this.singleValue) {
      this.selected = value;
    }
  }

  // Registers a callback function that should be called when the control's value changes in the UI.
  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  // Registers a callback function that should be called when the control receives a blur event.
  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  // This function is called by the forms API when the control status changes to or from "DISABLED".
  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  selectItem(item) {
    if (this.disabled) return;
    (document.activeElement as HTMLElement).blur();
    if (this._onTouched) {
      this._onTouched();
    }

    // for multi just flip the selected value
    if (this.multi) {
      if (this.selected.find((i) => i === item)) {
        this.selected = this.selected.filter((i) => i !== item);
      } else {
        this.selected = this.selected.concat([item]);
      }

      // for single select flip value of current item and reset all else
    } else {
      if (this.singleValue) {
        if (this.selected === item) {
          this.selected = undefined;
        } else {
          this.selected = item;
        }
      } else {
        if (this.selected.find((i) => i === item)) {
          this.selected = [];
        } else {
          this.selected = [item];
        }
      }
    }

    if (this._onChange) {
      this._onChange(this.selected);
    }
    this.bubbleToggle.emit(this.selected);
  }

  isSelected(item) {
    if (this.singleValue) {
      return this.selected === item;
    } else {
      return this.selected.find((i) => i === item);
    }
  }
}
